// REV-21370 / REV-21364 — the shared vocabulary of the correction-booking flow (ADR-0015).
//
// TWO pure views speak it and neither owns it: the closing-wide list (REV-21370) and the in-account
// list (REV-21364). ADR-0015 §9 is explicit that they are two components with different columns,
// different controls and different widths; what they SHARE is the row type and the status display,
// and this file is where that shared half lives — so neither view has to reach into the other's
// folder for a type.
//
// Nothing here is computed by a view. The upstream owner of every field is `closing_adjustments`
// (REV-21375, ADR-0015 §1); the three result figures are computed on every read by the backend's
// pure function (§11) and arrive as ready numbers.
import { SortDirection, SortState } from '../components/sort-header/sort-header.types';

/**
 * The three states of a correction booking — ADR-0015 §2.
 *
 * ⚠️ **THREE, not two.** The prototype's closing-wide list shows only `Offen`/`Gebucht`; that is a
 * gap in the prototype, not a decision, and the ADR overrules it. Without the middle state a
 * correction that has been handed to the ERP but not yet confirmed would be indistinguishable from
 * one nobody has touched — and that difference is the reason the whole flow exists.
 *
 * ⚠️ **No view sets `booked`.** The transition comes out of the imported ERP journal. The one
 * exception is the ambiguity picker on the detail surface, and it does not break the rule: the user
 * says WHICH of the journal lines the import contained is the proof, never that a proof exists.
 */
/**
 * The four states of a correction (V2 of the ticket).
 *
 * `proposed` is UNREACHABLE today — nothing in the backend writes it, because no agent proposes
 * corrections in this flow. It is declared so that the agent source later changes behaviour instead
 * of migrating stored states.
 *
 * `released` was `open` until 02.09.2026. Not a translation: `proposed` is also un-transferred, so
 * "open" would name two states at once — and the transfer may act only on `released`.
 */
export type AdjustmentState = 'proposed' | 'released' | 'transferred' | 'booked';

/**
 * WHERE a correction came from — the `Quelle` column (ADR-0015 §8).
 *
 * `manual` on every row of the current year; `priorYear` on a prior-year row. `agent` is
 * **reserved**: the agent proposes no correction bookings in this flow, so nothing produces it.
 *
 * The column is built anyway, and that is a decision rather than an oversight. It is the connection
 * point: a later second source becomes a DATA change instead of a change to table head, sorting,
 * filter, detail surface, translations and the tests of two components. A reviewer reading it as dead
 * code has to be able to find the reasoning — this is one of the places it lives.
 *
 * ⚠️ What a source must NEVER imply: a confidence, a reasoning trail, a proposal state, or an
 * accept/reject control. It is a provenance statement, not half a proposal flow. The lab specs assert
 * every one of those absences.
 */
/**
 * WHO stands behind the correction now — a STATE, not a record of origin (V2).
 *
 * `user` was `manual`. The rename follows the semantics: a booking an agent proposed is `agent`
 * until a person edits it, and from then on the person answers for its content. A row shows exactly
 * one value, never both.
 *
 * ⚠️ `priorYear` is NOT a source the backend ever sends. It is synthesised in `fromWire` from the
 * read-only flag, purely to render the `VJ` marking, and it rides on this field because the marking
 * shares the column. V2 defines source as two values, so this third one is a display concern in a
 * contract field — it should become its own flag, and that is a follow-up rather than part of this
 * change.
 */
export type AdjustmentSource = 'user' | 'agent' | 'priorYear';

/** How a correction reached the ERP. All three lead to `transferred` (ADR-0015 §3). */
/**
 * The two transfer targets.
 *
 * ⚠️ `csv` was removed on 03.09.2026 on request. Both remaining targets still land on the SAME state
 * (`Übertragen`), which was always the point of having more than one: how a correction reached the
 * ERP is recorded, but it does not change what the correction then is.
 */
export type AdjustmentTransferMethod = 'erpApi' | 'markedManual';

/**
 * An amount, as a view renders it.
 *
 * MINOR UNITS, like everywhere else in this flow (`money-and-materiality.md`): the view formats, it
 * never re-rounds, and it never receives a float it has to trust.
 */
export interface AdjustmentAmount {
    /** Integer, in the minor unit of `currency`. `4550` is CHF 45.50. */
    minor: number;
    currency: string;
}

/**
 * An account as a row renders it.
 *
 * Number and name as separate fields rather than one pre-composed label, because the two lists put
 * them in DIFFERENT columns: the closing-wide list shows `Soll`/`Haben` as bare numbers, and the
 * in-account list shows `GK` and `GK Bezeichnung` side by side.
 */
export interface AdjustmentAccountRef {
    id: string;
    number: string;
    name: string;
}

/**
 * ONE correction booking, as BOTH lists render it — the shared row type of ADR-0015 §9.
 *
 * Every field arrives ready. The views format amounts and dates for display and derive nothing else:
 * no status, no total, no sign, no relevance.
 */
export interface AdjustmentRow {
    /** What this correction does to the year result — the `P&L` column. From the server. */
    resultEffect: AdjustmentResultEffect;

    id: string;
    state: AdjustmentState;
    source: AdjustmentSource;
    /**
     * The reconciliation looked and did not find this booking in the ERP journal (V2/AC6).
     *
     * A named state, not an error, and mutually exclusive with `ambiguous`. Both false on a
     * transferred row means no import has run since the transfer.
     */
    notFound?: boolean;
    /**
     * WITHDRAWN — `Verworfen`. Not a state: the row keeps the state it was withdrawn from, so the
     * surfaces can show both "was released" and "was taken back".
     */
    discarded?: boolean;
    /** The booking text. */
    text: string;
    /** ISO `YYYY-MM-DD`. The view formats it; it never parses or re-times it. */
    date: string;

    debit: AdjustmentAccountRef;
    credit: AdjustmentAccountRef;
    /** The Q10 attribution account. `null` when the debit account stands in for it. */
    owner: AdjustmentAccountRef | null;

    amount: AdjustmentAmount;
    /** The mandate's VAT code, or absent. Rendered as-is — never defaulted to a plausible one. */
    vatCode?: string | null;

    /**
     * The optional remark (AC8 of REV-21370).
     *
     * On the ROW type rather than on a detail-only shape, because the detail surface renders the same
     * correction the list does and a second type for one extra field is a second thing to keep in step.
     * The two lists simply do not have a column for it.
     */
    remark?: string | null;

    /**
     * Several journal lines matched all four fields and nothing was guessed (ADR-0015 §4).
     *
     * A VISIBLE, named state: the row says so, and the detail surface offers the candidates. Absent or
     * `false` is the ordinary case.
     */
    ambiguous?: boolean;

    /**
     * WHO last touched it — shown in place of the source marking on the detail surface (§6).
     *
     * `editedBy` when there has been an edit, otherwise the person who entered it. Pre-resolved to a
     * display NAME by the container: a pure view resolves no user.
     */
    enteredBy?: string;
    editedBy?: string | null;

    /**
     * Whether this row may still be edited or discarded.
     *
     * Served by the backend, where the rule lives (§7) — the view does not derive it from `state`,
     * because then two places would own the same rule and the backend's would be the one that is
     * actually enforced. The view only decides whether to RENDER the control.
     */
    editable: boolean;
}

/** The three filter pills of the closing-wide list. A true partition of the year's corrections. */
/**
 * The five filter pills.
 *
 * ⚠️ `discarded` is NOT a state — it is a VIEW over the same rows. A withdrawn correction keeps the
 * state it was withdrawn from, so the list still shows whether it had been released or transferred,
 * and the state counts stay `all === released + transferred + booked`.
 */
export type AdjustmentFilterKey = 'all' | 'released' | 'transferred' | 'booked' | 'discarded';

/**
 * The counts behind the pills — one per state, plus `all`.
 *
 * `all === open + transferred + booked`, and it adds up because every state has a pill. An earlier
 * revision had three pills and documented the gap as intentional; it was not. The prototype carries
 * an `Übertragen (n)` pill of its own, and without it a transferred correction was reachable only
 * through `Alle` — the one state whose whole point is that the user is waiting on it.
 *
 * The backend has always returned all four (`ClosingAdjustmentCounts`); only this surface dropped one.
 * The view renders the numbers and adds nothing up.
 */
export interface AdjustmentFilterCounts {
    all: number;
    released: number;
    transferred: number;
    booked: number;
    /**
     * The WITHDRAWN corrections — deliberately NOT part of `all`.
     *
     * `all === released + transferred + booked` has to keep holding, and a withdrawn booking is not a
     * fourth state: it retains the state it was withdrawn from. So the pill counts a view, not a rung.
     */
    discarded: number;
}

/**
 * What one correction does to the year result — the prototype's `P&L` column.
 *
 * Served per row by the backend, from the same predicate as the three result figures, so the column
 * and the totals cannot disagree. `none` covers two cases that look different and are the same
 * answer: neither account is an income-statement account, or both are and they cancel.
 */
/**
 * What one correction does to the year result.
 *
 * `excluded` is the absence of an answer, not a fourth effect: one of the accounts is not in the
 * mandate's chart, so the booking is left out of the computed impact and says so (V2/AC12). It used
 * to render as `none`, which is a different claim — "does not affect the result".
 */
export type AdjustmentResultEffect = 'increase' | 'decrease' | 'none' | 'excluded';

/** The sortable columns of the closing-wide list, in the prototype's order. */
export type AdjustmentSortKey = 'text' | 'debit' | 'credit' | 'amount' | 'pnl' | 'source' | 'state';

export type AdjustmentSortDirection = SortDirection;

export type AdjustmentSort = SortState<AdjustmentSortKey>;

/**
 * The three derived result rows plus the list total — computed by the backend, never here (§11).
 *
 * `impactMinor` is SIGNED. `resultAfterMinor` may be negative, and the view renders that as a
 * negative result rather than as an error.
 */
export interface AdjustmentResultRows {
    currency: string;
    /** `Provisorisches Jahresergebnis · vor Korrekturbuchungen`. */
    provisionalMinor: number;
    /** `Einfluss ergebniswirksamer Korrekturen` — signed. */
    impactMinor: number;
    /** `Jahresergebnis nach Korrekturbuchungen`. */
    resultAfterMinor: number;
    /** `Total Korrekturbuchungen` — every listed amount, result-relevant or not. */
    totalMinor: number;
    /**
     * How many listed corrections touch an account that is not classified yet.
     *
     * `0` is the ordinary case. Above zero the surface says so, because an unclassified account has no
     * statement and therefore contributes to the total and not to the impact — a figure that is
     * quietly incomplete is worse than one that admits it.
     */
    unclassifiedCount?: number;
}

/**
 * What the transfer control may offer — a QUERIED capability, never a constant (ADR-0015 §3a).
 *
 * ⚠️ `Als manuell gebucht markieren` is not a field here because it
 * are ALWAYS available, which is precisely what they exist for. Only the API target is a question.
 */
export interface AdjustmentTransferOptions {
    /** `Direkt ans ERP senden` — enabled only when this is `true`. */
    canWriteToErp: boolean;
    /** `null` iff `canWriteToErp`. Otherwise a translation KEY for the tooltip. */
    reasonKey: string | null;
    /**
     * The connected system's NAME, from the mandate's own connection.
     *
     * `null` when nothing is connected — the surface then says so instead of inventing a name.
     *
     * ⚠️ The prototype writes "bexio" because its demo company sits there. aumico is connected to
     * four accounting systems, and for an Abacus or Topal mandate that label is simply false. A
     * hard-coded system name in a template, a translation or a test is a defect.
     */
    connectedName: string | null;
}

/** ONE candidate journal line of an ambiguous match — what the detail surface offers (AC9). */
export interface AdjustmentCandidate {
    journalEntryId: string;
    /** ISO `YYYY-MM-DD`, as the ERP dated it. */
    date: string;
    description: string | null;
    amount: AdjustmentAmount;
    /**
     * The ERP's document number, or `null`.
     *
     * `null` today for every vendor: the normalised journal carries no document number, and reaching
     * past it into a vendor's raw response would break the neutrality of §3a. The view renders `—`,
     * which is what "we do not hold this" looks like.
     */
    documentNumber: string | null;
}

// ── Event payloads ───────────────────────────────────────────────────────────────────────────────
//
// Every one of these is a REPORT. Whether it takes effect is the connecting layer's decision
// (REV-21375) — no state change, no amount and no row comes into existence in a view.

/** `Neue Buchung`, submitted. Amounts in minor units, like everywhere else. */
export interface AdjustmentCreateEvent {
    /**
     * The booking date, `YYYY-MM-DD`, or `null` to let the server use the balance sheet date.
     *
     * NOT the creation timestamp — the server records that itself and it is not an input at all.
     */
    date: string | null;
    text: string;
    debitAccountId: string;
    creditAccountId: string;
    amountMinor: number;
    currency: string;
    /** `null` = `— automatisch (Soll) —`: attribute the correction to the debit account. */
    ownerAccountId: string | null;
    remark: string | null;
}

/** An OPEN correction, edited. Only what changed. */
export interface AdjustmentEditEvent {
    adjustmentId: string;
    text: string;
    debitAccountId: string;
    creditAccountId: string;
    amountMinor: number;
    ownerAccountId: string | null;
    remark: string | null;
    /** The booking date, `YYYY-MM-DD`. Editable inside the fiscal year (V2/AC3). */
    date: string;
}

/** `Übertragen (n)` — a SET operation with one of three targets (§3). */
export interface AdjustmentTransferEvent {
    adjustmentIds: string[];
    method: AdjustmentTransferMethod;
}

/** The ambiguity pick (AC9). Only a candidate the reconciliation found can be named. */
export interface AdjustmentAmbiguityResolveEvent {
    adjustmentId: string;
    journalEntryId: string;
}

/**
 * An account as the entry modal's two pickers list it.
 *
 * Its own shape rather than {@link AdjustmentAccountRef}: a picker offers a searchable list and shows
 * the position, while a row shows a resolved account. `group` is matched on but never rendered — a
 * user who thinks in balance-sheet positions has no other way to find the account.
 */
export interface AdjustmentAccountOption {
    id: string;
    number: string;
    name: string;
    group?: string | null;
}
